/**
 * <blog-search> - progressive free-text search for the blog archive.
 *
 * Normal archive and tagged pages remain Shopify server-rendered and paginated.
 * Free-text search loads the archive's real paginated HTML on demand, so it
 * does not inherit a hard 250-article ceiling.
 */
(function () {
  if (customElements.get('blog-search')) return;

  function normalize(value) {
    var s = String(value || '').toLowerCase();
    if (s.normalize) s = s.normalize('NFD');
    var out = '';
    for (var i = 0; i < s.length; i++) {
      var code = s.charCodeAt(i);
      if (code >= 768 && code <= 879) continue;
      var isWord = (code >= 97 && code <= 122) || (code >= 48 && code <= 57) || code > 191;
      out += isWord ? s[i] : ' ';
    }
    return out.replace(/ +/g, ' ').trim();
  }

  class BlogSearch extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.input = this.querySelector('[data-search-input]');
      this.topic = this.querySelector('[data-search-topic]');
      this.clearBtn = this.querySelector('[data-search-clear]');
      this.clearAllBtn = this.querySelector('[data-search-clear-all]');
      this.countEl = this.querySelector('[data-search-count]');
      this.grid = this.querySelector('[data-search-grid]');
      this.emptyEl = this.querySelector('[data-search-empty]');
      this.paginationEl = this.querySelector('[data-search-pagination]');
      this.perPage = parseInt(this.dataset.perPage, 10) || 12;
      this.totalPages = Math.max(1, parseInt(this.dataset.totalPages, 10) || 1);
      this.currentPage = Math.max(1, parseInt(this.dataset.currentPage, 10) || 1);

      if (!this.input || !this.grid) return;

      this.initialGridHtml = this.grid.innerHTML;
      this.initialPaginationHtml = this.paginationEl ? this.paginationEl.innerHTML : '';
      this.initialCount = this.countEl ? this.countEl.textContent : '';
      this.searchItems = null;
      this.searchPage = 1;
      this.requestToken = 0;

      this.bindEvents();
      this.readUrl();

      if (this.input.value.trim()) this.searchAll();
      else this.restoreServerPage();
    }

    bindEvents() {
      var self = this;
      var timer;

      this.input.addEventListener('input', function () {
        self.toggleButtons();
        clearTimeout(timer);
        timer = setTimeout(function () {
          if (self.input.value.trim()) self.searchAll();
          else self.clearSearchState();
        }, 250);
      });

      this.input.addEventListener('keydown', function (event) {
        if (event.key === 'Enter') event.preventDefault();
        if (event.key === 'Escape') self.clearSearch();
      });

      if (this.topic) {
        this.topic.addEventListener('change', function () {
          var target = new URL(self.topic.value || window.location.pathname, window.location.origin);
          var query = self.input.value.trim();
          if (query) target.searchParams.set('q', query);
          window.location.assign(target.toString());
        });
      }

      if (this.clearBtn) this.clearBtn.addEventListener('click', function () { self.clearSearch(); });
      if (this.clearAllBtn) this.clearAllBtn.addEventListener('click', function () { self.reset(); });

      var resetBtn = this.querySelector('[data-search-reset]');
      if (resetBtn) resetBtn.addEventListener('click', function () { self.reset(); });

      if (this.paginationEl) {
        this.paginationEl.addEventListener('click', function (event) {
          var button = event.target.closest('[data-search-page]');
          if (!button) return;
          self.searchPage = parseInt(button.dataset.searchPage, 10) || 1;
          self.renderSearchResults();
          self.writeSearchUrl(true);
          self.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }

      window.addEventListener('popstate', function () {
        self.readUrl();
        if (self.input.value.trim()) self.searchAll();
        else self.restoreServerPage();
      });
    }

    readUrl() {
      var params = new URLSearchParams(window.location.search);
      this.input.value = params.get('q') || '';
      this.searchPage = Math.max(1, parseInt(params.get('search_page'), 10) || 1);
      this.toggleButtons();
    }

    toggleButtons() {
      var hasQuery = this.input.value.trim() !== '';
      var hasTopic = this.topic ? this.topic.selectedIndex > 0 : false;
      if (this.clearBtn) this.clearBtn.hidden = !hasQuery;
      if (this.clearAllBtn) this.clearAllBtn.disabled = !hasQuery && !hasTopic;
    }

    clearSearch() {
      this.input.value = '';
      this.clearSearchState();
      this.input.focus();
    }

    reset() {
      var target = this.topic && this.topic.options.length ? this.topic.options[0].value : window.location.pathname;
      window.location.assign(new URL(target, window.location.origin).toString());
    }

    clearSearchState() {
      this.searchItems = null;
      this.searchPage = 1;
      this.requestToken++;
      this.restoreServerPage();
      var url = new URL(window.location.href);
      url.searchParams.delete('q');
      url.searchParams.delete('search_page');
      window.history.replaceState(window.history.state, '', url);
      this.toggleButtons();
    }

    restoreServerPage() {
      this.grid.innerHTML = this.initialGridHtml;
      this.grid.hidden = false;
      if (this.emptyEl) this.emptyEl.hidden = true;
      if (this.paginationEl) this.paginationEl.innerHTML = this.initialPaginationHtml;
      if (this.countEl) this.countEl.textContent = this.initialCount;
      this.toggleButtons();
    }

    async searchAll() {
      var token = ++this.requestToken;
      var query = normalize(this.input.value);
      if (!query) {
        this.clearSearchState();
        return;
      }

      this.toggleButtons();
      if (this.countEl) this.countEl.textContent = 'Searching guides...';

      try {
        var pages = [];
        for (var page = 1; page <= this.totalPages; page++) pages.push(page);

        var allItems = this.extractItems(document);
        var pagesToFetch = pages.filter(function (page) {
          return page !== this.currentPage;
        }, this);

        for (var start = 0; start < pagesToFetch.length; start += 6) {
          var batch = pagesToFetch.slice(start, start + 6);
          var docs = await Promise.all(batch.map(this.fetchPage.bind(this)));
          if (token !== this.requestToken) return;
          docs.forEach(function (doc) {
            allItems = allItems.concat(this.extractItems(doc));
          }, this);
        }

        if (token !== this.requestToken) return;

        var seen = new Set();
        allItems = allItems.filter(function (item) {
          var key = item.url || item.title + '|' + item.text;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });

        allItems.forEach(function (item, index) { item.order = index; });

        var words = query.split(' ').filter(Boolean);
        this.searchItems = allItems.filter(function (item) {
          for (var i = 0; i < words.length; i++) {
            if (item.text.indexOf(words[i]) === -1) return false;
          }
          return true;
        }).sort(function (a, b) {
          var aTitle = a.title.indexOf(query) !== -1 ? 1 : 0;
          var bTitle = b.title.indexOf(query) !== -1 ? 1 : 0;
          return bTitle - aTitle || a.order - b.order;
        });

        this.renderSearchResults();
        this.writeSearchUrl(false);
      } catch (error) {
        console.error('[blog-search] Could not search the full archive:', error);
        if (token !== this.requestToken) return;
        this.restoreServerPage();
        if (this.countEl) this.countEl.textContent = 'Search unavailable. Showing this page.';
      }
    }

    async fetchPage(page) {
      var url = new URL(window.location.href);
      url.searchParams.delete('q');
      url.searchParams.delete('search_page');
      url.searchParams.set('page', String(page));

      var response = await fetch(url.toString(), { credentials: 'same-origin' });
      if (!response.ok) throw new Error('HTTP ' + response.status);

      return new DOMParser().parseFromString(await response.text(), 'text/html');
    }

    extractItems(root) {
      return Array.from(root.querySelectorAll('blog-search [data-search-grid] [data-search-item]')).map(function (el) {
        var heading = el.querySelector('.card__heading, h2, h3');
        var link = el.querySelector('a[href]');
        return {
          html: el.outerHTML,
          title: normalize(heading ? heading.textContent : ''),
          text: normalize((el.dataset.search || '') + ' ' + el.textContent),
          url: link ? link.href : '',
          order: 0
        };
      });
    }

    renderSearchResults() {
      var items = this.searchItems || [];
      var total = items.length;
      var pages = Math.max(1, Math.ceil(total / this.perPage));
      this.searchPage = Math.min(Math.max(this.searchPage, 1), pages);

      var start = (this.searchPage - 1) * this.perPage;
      var visible = items.slice(start, start + this.perPage);

      this.grid.innerHTML = visible.map(function (item) { return item.html; }).join('');
      this.grid.hidden = total === 0;

      if (this.emptyEl) this.emptyEl.hidden = total !== 0;
      if (this.countEl) this.countEl.textContent = total + (total === 1 ? ' guide' : ' guides');

      this.renderSearchPagination(pages);
    }

    renderSearchPagination(pages) {
      if (!this.paginationEl) return;
      if (pages <= 1) {
        this.paginationEl.innerHTML = '';
        return;
      }

      var current = this.searchPage;
      var html = '<nav class="pagination" aria-label="Search results pagination"><ul class="pagination__list list-unstyled" role="list">';

      if (current > 1) {
        html += '<li><button type="button" class="pagination__item pagination__item-arrow link" data-search-page="' + (current - 1) + '" aria-label="Previous search results page">&lsaquo;</button></li>';
      }

      for (var page = 1; page <= pages; page++) {
        var nearby = page === 1 || page === pages || Math.abs(page - current) <= 1;
        if (!nearby) {
          if (page === 2 || page === pages - 1) html += '<li><span class="pagination__item">&hellip;</span></li>';
          continue;
        }

        html += page === current
          ? '<li><span class="pagination__item pagination__item--current" aria-current="page">' + page + '</span></li>'
          : '<li><button type="button" class="pagination__item link" data-search-page="' + page + '" aria-label="Search results page ' + page + '">' + page + '</button></li>';
      }

      if (current < pages) {
        html += '<li><button type="button" class="pagination__item pagination__item-arrow link" data-search-page="' + (current + 1) + '" aria-label="Next search results page">&rsaquo;</button></li>';
      }

      html += '</ul></nav>';
      this.paginationEl.innerHTML = html;
    }

    writeSearchUrl(push) {
      var url = new URL(window.location.href);
      var query = this.input.value.trim();

      url.searchParams.delete('page');
      if (query) url.searchParams.set('q', query);
      else url.searchParams.delete('q');

      if (this.searchPage > 1) url.searchParams.set('search_page', String(this.searchPage));
      else url.searchParams.delete('search_page');

      if (url.href !== window.location.href) {
        window.history[push ? 'pushState' : 'replaceState'](window.history.state, '', url);
      }
    }
  }

  customElements.define('blog-search', BlogSearch);
})();